import React, { useRef, useEffect, useState } from 'react';
import { useSpeakingChatbotV2 } from '../hooks/useSpeakingChatbotV2';
import type { Message } from '../hooks/useSpeakingChatbotV2';
import AgentInfoPanel from './AgentInfoPanel';
import './SpeakingChatbotV2Assistant.css';

interface SpeakingChatbotV2AssistantProps {
  projectId?: string;
  entityType: string;
  entityId: string;
  entityName: string;
  fieldConfig: Record<string, string>;
  fieldLabels: Record<string, { label: string; description: string }>;
  kbId?: string;
  kbName?: string;
  projectName: string;
  documentIds?: string[];
  language?: 'en' | 'fr';
  activeTab: string;
  activeField: string | null;
  onFieldUpdate: (fieldName: string, value: string) => void;
  getFieldValue: (fieldName: string) => string;
  onContextFieldChanged?: (targetField: string) => void;
  onExtractionResults?: (data: unknown) => void;
  onPilotHandoff?: () => void;
  onDraftUpdated?: () => void;
  onFtoUpdated?: () => void;
  /** Agent-driven tab switching: called when the agent emits a switch_tab event. */
  onSwitchTab?: (tab: string) => void;
  showMetrics?: boolean;
  solutionId?: string;
  initialMessage?: string | null;
  proactiveInit?: boolean;
}

const SpeakingChatbotV2Assistant: React.FC<SpeakingChatbotV2AssistantProps> = ({
  projectId,
  entityType,
  entityId,
  entityName,
  fieldConfig,
  fieldLabels,
  kbId,
  kbName,
  projectName,
  documentIds,
  language = 'fr',
  activeTab,
  activeField,
  onFieldUpdate,
  getFieldValue,
  onContextFieldChanged,
  onExtractionResults,
  onPilotHandoff,
  onDraftUpdated,
  onFtoUpdated,
  onSwitchTab,
  showMetrics = false,
  solutionId,
  initialMessage,
  proactiveInit = false,
}) => {
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [inputText, setInputText] = useState('');
  const [metricsExpanded, setMetricsExpanded] = useState(true);

  const {
    messages,
    isProcessing,
    statusMessage,
    isStarting,
    isPlaying,
    isRecording,
    isConversationMode,
    partialTranscript,
    audioLevel,
    metrics,
    availableDevices,
    selectedDeviceId,
    setSelectedDeviceId,
    startChat,
    commitSpeech,
    stopChat,
    sendTextMessage,
  } = useSpeakingChatbotV2({
    projectId,
    entityType,
    entityId,
    entityName,
    fieldConfig,
    fieldLabels,
    kbId,
    kbName,
    projectName,
    documentIds,
    language,
    activeTab,
    activeField,
    onFieldUpdate,
    getFieldValue,
    onContextFieldChanged,
    onExtractionResults,
    onPilotHandoff,
    onDraftUpdated,
    onFtoUpdated,
    onSwitchTab,
    solutionId,
    proactiveInit,
  });

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, partialTranscript]);

  // Handle text input submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputText.trim() && !isProcessing) {
      sendTextMessage(inputText.trim());
      setInputText('');
    }
  };

  // Mike button: 3-way dispatch depending on conversation state
  const handleMikeClick = async () => {
    if (!isConversationMode) {
      await startChat();       // enter conversation mode + start recording
    } else if (isRecording) {
      commitSpeech();          // done speaking — commit, stay in mode
    } else {
      stopChat();              // exit conversation mode
    }
  };

  return (
    <div className="scv2-assistant">
      {/* Header */}
      <div className="scv2-assistant-header">
        <div className="header-left">
          <h3>Chatbot</h3>
          <div className="audio-meter">
            <div
              className="audio-meter-fill"
              style={{ width: `${Math.min(audioLevel * 1.5, 100)}%` }}
            />
          </div>
        </div>
        <div className="header-right">
          <AgentInfoPanel
            activeField={activeField}
            entityType={entityType}
            canEdit={Object.values(fieldConfig).some(v => v === 'editable')}
            kbId={kbId}
            kbName={kbName}
            solutionId={solutionId}
            fieldLabels={fieldLabels}
            showMetrics={showMetrics}
          />
          <select
            className="device-select"
            value={selectedDeviceId || ''}
            onChange={(e) => setSelectedDeviceId(e.target.value)}
            disabled={isRecording}
          >
            {availableDevices.map((device: MediaDeviceInfo) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label || `Mic ${device.deviceId.substring(0, 5)}`}
              </option>
            ))}
          </select>
          <button
            className={`voice-btn ${isRecording ? 'recording' : ''} ${isStarting ? 'starting' : ''} ${isConversationMode && !isRecording ? 'conversation-active' : ''}`}
            onClick={handleMikeClick}
            disabled={isStarting}
            title={!isConversationMode ? 'Start voice conversation' : isRecording ? 'Done speaking' : 'Exit conversation mode'}
          >
            {isStarting ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8z" fill="#868e96" opacity="0.3"/>
                <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10" stroke="#495057" strokeWidth="2" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                </path>
              </svg>
            ) : isRecording ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="6" y="6" width="12" height="12" rx="2" fill="#fa5252"/>
              </svg>
            ) : isConversationMode ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 14C13.6569 14 15 12.6569 15 11V7C15 5.34315 13.6569 4 12 4C10.3431 4 9 5.34315 9 7V11C9 12.6569 10.3431 14 12 14Z" fill="#2f9e44"/>
                <path d="M6 10C6.55228 10 7 10.4477 7 11V12C7 14.7614 9.23858 17 12 17C14.7614 17 17 14.7614 17 12V11C17 10.4477 17.4477 10 18 10C18.5523 10 19 10.4477 19 11V12C19 15.5208 16.3304 18.4376 13 18.9291V20H15C15.5523 20 16 20.4477 16 21C16 21.5523 15.5523 22 15 22H9C8.44772 22 8 21.5523 8 21C8 20.4477 8.44772 20 9 20H11V18.9291C7.66962 18.4376 5 15.5208 5 12V11C5 10.4477 5.44772 10 6 10Z" fill="#2f9e44"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 14C13.6569 14 15 12.6569 15 11V7C15 5.34315 13.6569 4 12 4C10.3431 4 9 5.34315 9 7V11C9 12.6569 10.3431 14 12 14Z" fill="#495057"/>
                <path d="M6 10C6.55228 10 7 10.4477 7 11V12C7 14.7614 9.23858 17 12 17C14.7614 17 17 14.7614 17 12V11C17 10.4477 17.4477 10 18 10C18.5523 10 19 10.4477 19 11V12C19 15.5208 16.3304 18.4376 13 18.9291V20H15C15.5523 20 16 20.4477 16 21C16 21.5523 15.5523 22 15 22H9C8.44772 22 8 21.5523 8 21C8 20.4477 8.44772 20 9 20H11V18.9291C7.66962 18.4376 5 15.5208 5 12V11C5 10.4477 5.44772 10 6 10Z" fill="#495057"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Status Bar */}
      <div className="scv2-status-bar">
        {isRecording && (
          <span className="status-badge recording">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '4px', verticalAlign: 'middle'}}>
              <path d="M8 10C9.10457 10 10 9.10457 10 8V4C10 2.89543 9.10457 2 8 2C6.89543 2 6 2.89543 6 4V8C6 9.10457 6.89543 10 8 10Z" fill="currentColor"/>
              <path d="M4 7C4.27614 7 4.5 7.22386 4.5 7.5V8C4.5 9.933 6.067 11.5 8 11.5C9.933 11.5 11.5 9.933 11.5 8V7.5C11.5 7.22386 11.7239 7 12 7C12.2761 7 12.5 7.22386 12.5 7.5V8C12.5 10.3472 10.7767 12.2945 8.5 12.4776V13.5H10C10.2761 13.5 10.5 13.7239 10.5 14C10.5 14.2761 10.2761 14.5 10 14.5H6C5.72386 14.5 5.5 14.2761 5.5 14C5.5 13.7239 5.72386 13.5 6 13.5H7.5V12.4776C5.22329 12.2945 3.5 10.3472 3.5 8V7.5C3.5 7.22386 3.72386 7 4 7Z" fill="currentColor"/>
            </svg>
            Listening...
          </span>
        )}
        {isProcessing && (
          <span className="status-badge processing">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '4px', verticalAlign: 'middle'}}>
              <path d="M8 3C7.72386 3 7.5 2.77614 7.5 2.5C7.5 2.22386 7.72386 2 8 2C8.27614 2 8.5 2.22386 8.5 2.5C8.5 2.77614 8.27614 3 8 3Z" fill="currentColor"/>
              <path d="M11 4.5C10.7239 4.5 10.5 4.27614 10.5 4C10.5 3.72386 10.7239 3.5 11 3.5C11.2761 3.5 11.5 3.72386 11.5 4C11.5 4.27614 11.2761 4.5 11 4.5Z" fill="currentColor" opacity="0.9"/>
              <path d="M12.5 8C12.2239 8 12 7.77614 12 7.5C12 7.22386 12.2239 7 12.5 7C12.7761 7 13 7.22386 13 7.5C13 7.77614 12.7761 8 12.5 8Z" fill="currentColor" opacity="0.8"/>
              <path d="M11 11.5C10.7239 11.5 10.5 11.2761 10.5 11C10.5 10.7239 10.7239 10.5 11 10.5C11.2761 10.5 11.5 10.7239 11.5 11C11.5 11.2761 11.2761 11.5 11 11.5Z" fill="currentColor" opacity="0.7"/>
              <path d="M8 13C7.72386 13 7.5 12.7761 7.5 12.5C7.5 12.2239 7.72386 12 8 12C8.27614 12 8.5 12.2239 8.5 12.5C8.5 12.7761 8.27614 13 8 13Z" fill="currentColor" opacity="0.6"/>
              <path d="M5 11.5C4.72386 11.5 4.5 11.2761 4.5 11C4.5 10.7239 4.72386 10.5 5 10.5C5.27614 10.5 5.5 10.7239 5.5 11C5.5 11.2761 5.27614 11.5 5 11.5Z" fill="currentColor" opacity="0.5"/>
              <path d="M3.5 8C3.22386 8 3 7.77614 3 7.5C3 7.22386 3.22386 7 3.5 7C3.77614 7 4 7.22386 4 7.5C4 7.77614 3.77614 8 3.5 8Z" fill="currentColor" opacity="0.4"/>
              <path d="M5 4.5C4.72386 4.5 4.5 4.27614 4.5 4C4.5 3.72386 4.72386 3.5 5 3.5C5.27614 3.5 5.5 3.72386 5.5 4C5.5 4.27614 5.27614 4.5 5 4.5Z" fill="currentColor" opacity="0.3"/>
            </svg>
            {statusMessage || 'Thinking...'}
          </span>
        )}
        {isPlaying && (
          <span className="status-badge playing">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginRight: '4px', verticalAlign: 'middle'}}>
              <path d="M2 5.5C2 5.22386 2.22386 5 2.5 5H4.5C4.77614 5 5 5.22386 5 5.5V10.5C5 10.7761 4.77614 11 4.5 11H2.5C2.22386 11 2 10.7761 2 10.5V5.5Z" fill="currentColor"/>
              <path d="M6 3.5C6 3.22386 6.22386 3 6.5 3H8.5C8.77614 3 9 3.22386 9 3.5V12.5C9 12.7761 8.77614 13 8.5 13H6.5C6.22386 13 6 12.7761 6 12.5V3.5Z" fill="currentColor"/>
              <path d="M10 6.5C10 6.22386 10.2239 6 10.5 6H12.5C12.7761 6 13 6.22386 13 6.5V9.5C13 9.77614 12.7761 10 12.5 10H10.5C10.2239 10 10 9.77614 10 9.5V6.5Z" fill="currentColor"/>
            </svg>
            Speaking...
          </span>
        )}
        {!isRecording && !isProcessing && !isPlaying && isConversationMode && (
          <span className="status-badge conversation-idle">Ready to listen...</span>
        )}
        {!isRecording && !isProcessing && !isPlaying && !isConversationMode && (
          <span className="status-badge idle">Ready</span>
        )}
      </div>

      {/* Context Window Info - Only shown for authorized users */}
      {showMetrics && metrics && metrics.llm_input_tokens > 0 && (
        <div className="scv2-context-info">
          <span className="context-label">Context:</span>
          <span className="context-value">
            {metrics.llm_input_tokens.toLocaleString()} / {metrics.llm_context_window_size.toLocaleString()}
          </span>
          <span className="context-unit">tokens</span>
          {metrics.llm_context_window_size > 0 && (
            <span className="context-percentage" style={{
              color: (metrics.llm_input_tokens / metrics.llm_context_window_size) > 0.8 ? '#fa5252' : 
                     (metrics.llm_input_tokens / metrics.llm_context_window_size) > 0.6 ? '#fd7e14' : '#868e96'
            }}>
              ({Math.round((metrics.llm_input_tokens / metrics.llm_context_window_size) * 100)}%)
            </span>
          )}
        </div>
      )}

      {/* Chat Messages */}
      <div className="scv2-chat-messages">
        {messages.length === 0 && !isRecording && !initialMessage && (
          <div className="empty-chat">
            <div className="empty-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 28C26.7614 28 29 25.7614 29 23V15C29 12.2386 26.7614 10 24 10C21.2386 10 19 12.2386 19 15V23C19 25.7614 21.2386 28 24 28Z" fill="#868e96"/>
                <path d="M12 20C12.8284 20 13.5 20.6716 13.5 21.5V23C13.5 27.6944 17.3056 31.5 22 31.5C26.6944 31.5 30.5 27.6944 30.5 23V21.5C30.5 20.6716 31.1716 20 32 20C32.8284 20 33.5 20.6716 33.5 21.5V23C33.5 29.0416 28.6592 33.9128 22.5 34.3882V37H26C26.8284 37 27.5 37.6716 27.5 38.5C27.5 39.3284 26.8284 40 26 40H18C17.1716 40 16.5 39.3284 16.5 38.5C16.5 37.6716 17.1716 37 18 37H21.5V34.3882C15.3408 33.9128 10.5 29.0416 10.5 23V21.5C10.5 20.6716 11.1716 20 12 20Z" fill="#868e96"/>
              </svg>
            </div>
            <h4>Ready to assist</h4>
            <p>Click the microphone button or type a message to start editing your project metadata.</p>
          </div>
        )}

        {/* Resume briefing (displayed before conversation messages) */}
        {initialMessage && messages.length === 0 && (
          <div className="chat-bubble assistant briefing">
            {initialMessage}
          </div>
        )}

        {messages.map((msg: Message, idx: number) => (
          <div key={idx} className={`chat-bubble ${msg.role}`}>
            {msg.content}
          </div>
        ))}

        {/* Partial transcript while speaking */}
        {isRecording && partialTranscript && (
          <div className="chat-bubble user partial">
            {partialTranscript}
            <span className="typing-indicator">...</span>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Input Area */}
      <div className="scv2-input-area">
        <form onSubmit={handleSubmit} className="text-input-form">
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder={language === 'fr' ? 'Tapez un message...' : 'Type a message...'}
            disabled={isProcessing || isRecording}
            className="text-input"
          />
          <button
            type="submit"
            disabled={!inputText.trim() || isProcessing || isRecording}
            className="send-btn"
          >
            ➤
          </button>
        </form>
      </div>

      {/* Performance Metrics Overlay - Only shown for admin users */}
      {showMetrics && metrics && (
        <div className="perf-metrics-overlay">
          <div className="perf-metrics-header" onClick={() => setMetricsExpanded(!metricsExpanded)}>
            <span>⏱️ Perf</span>
            <span className="perf-toggle">{metricsExpanded ? '▼' : '▲'}</span>
          </div>
          {metricsExpanded && (
            <div className="perf-metrics-body">
              <div className="perf-row">
                <span className="perf-label">Total:</span>
                <span className="perf-value">{metrics.total_turn_s.toFixed(2)}s</span>
              </div>
              <div className="perf-row">
                <span className="perf-label">TTFA:</span>
                <span className="perf-value">{metrics.time_to_first_audio_s.toFixed(2)}s</span>
              </div>
              <div className="perf-breakdown">
                <div className="perf-bar" title={`STT: ${metrics.stt_pct.toFixed(0)}%`}>
                  <div className="perf-segment stt" style={{width: `${metrics.stt_pct}%`}}></div>
                  <div className="perf-segment llm" style={{width: `${metrics.llm_pct}%`}}></div>
                  <div className="perf-segment tts" style={{width: `${metrics.tts_pct}%`}}></div>
                </div>
                <div className="perf-legend">
                  <span className="leg-stt">STT {(metrics.stt_duration_s * 1000).toFixed(0)}ms</span>
                  <span className="leg-llm">LLM {(metrics.llm_total_s * 1000).toFixed(0)}ms</span>
                  <span className="leg-tts">TTS {(metrics.tts_total_s * 1000).toFixed(0)}ms</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SpeakingChatbotV2Assistant;
